import React, { type Dispatch, type SetStateAction } from 'react';

import {
  AtomicFooterType,
  AtomicHeaderType,
  AtomicItemListType,
  type IScreensComponent,
} from '../../../api';
// Header - 직접 import (barrel 순환 참조 방지)
import { HeadlineSmallOneLine } from '../Header/HeadlineSmallOneLine';
import { HeadlineSmallOneLineWithText } from '../Header/HeadlineSmallOneLineWithText';
import { HeadlineSmallOneLineWithTextCompact } from '../Header/HeadlineSmallOneLineWithTextCompact';
import { HeadlineXsmallOneLine } from '../Header/HeadlineXsmallOneLine';
import { HeadlineOnlyText } from '../Header/HeadlineOnlyText';
import { HeadlineSmallTwoLineWithTimer } from '../Header/HeadlineSmallTwoLineWithTimer';
import { HeadlineReloadOneLine } from '../Header/HeadlineReloadOneLine';

// ItemList - 직접 import (barrel 순환 참조 방지)
import { ProfileInfo } from '../ItemList/ProfileInfo';
import { Divider } from '../ItemList/Divider';
import { GoodsCarousel } from '../ItemList/GoodsCarousel';
import { FilterList } from '../ItemList/FilterList';
import { TwoColGoodsList } from '../ItemList/TwoColGoodsList';
import { TwoColCardList } from '../ItemList/TwoColCardList';
import { ThreeColGoodsList } from '../ItemList/ThreeColGoodsList';
import { ThreeColCardList } from '../ItemList/ThreeColCardList';
import { TwoLineMessage } from '../ItemList/TwoLineMessage';
import { Proxy } from '../ItemList/Proxy';
import { Empty } from '../ItemList/Empty';
import { NotificationGuide } from '../ItemList/NotificationGuide';
import { CompanyInfo } from '../ItemList/CompanyInfo';
import { SearchBar } from '../ItemList/SearchBar';
import { NeonSignBoard } from '../ItemList/NeonSignBoard';
import { Summary } from '../ItemList/Summary';
import { HorizontalChipList } from '../ItemList/HorizontalChipList';
import { FourColIconList, FiveColIconList, ScrollableIconList } from '../ItemList/IconList';
import { MarketBadgeList } from '../ItemList/MarketBadgeList';
import { StoreList } from '../ItemList/StoreList';
import { TwoImageMarketCarousel, ThreeImageMarketCarousel } from '../ItemList/MarketCarousel';
import { RollingBannerList } from '../ItemList/RollingBannerList';
import { BannerCarousel } from '../ItemList/BannerCarousel';
import { DescriptionCardCarousel } from '../ItemList/DescriptionCardCarousel';
import { PhotoReviewCarousel } from '../ItemList/PhotoReviewCarousel';
import { VerticalBannerList } from '../ItemList/VerticalBannerList';

// Footer - 직접 import
import { FooterMore } from '../Footer/FooterMore';

export const HeaderComponent = {
  [AtomicHeaderType.HeadlineSmallOneLine]: HeadlineSmallOneLine,
  [AtomicHeaderType.HeadlineXsmallOneLine]: HeadlineXsmallOneLine,
  [AtomicHeaderType.HeadlineSmallOneLineWithText]: HeadlineSmallOneLineWithText,
  [AtomicHeaderType.HeadlineSmallOneLineWithTextCompact]:
    HeadlineSmallOneLineWithTextCompact,
  [AtomicHeaderType.HeadlineOnlyText]: HeadlineOnlyText,
  [AtomicHeaderType.HeadlineSmallTwoLineWithTimer]:
    HeadlineSmallTwoLineWithTimer,
  [AtomicHeaderType.HeadlineReloadOneLine]: HeadlineReloadOneLine,
};

export const ItemListComponent = {
  [AtomicItemListType.PROFILE_INFO]: ProfileInfo,
  [AtomicItemListType.Divider]: Divider,
  [AtomicItemListType.GoodsCarousel]: GoodsCarousel,
  [AtomicItemListType.FilterList]: FilterList,
  [AtomicItemListType.TwoColGoodsList]: TwoColGoodsList,
  [AtomicItemListType.TwoColCardList]: TwoColCardList,
  [AtomicItemListType.ThreeColGoodsList]: ThreeColGoodsList,
  [AtomicItemListType.ThreeColCardList]: ThreeColCardList,
  [AtomicItemListType.TwoLineMessage]: TwoLineMessage,
  [AtomicItemListType.Proxy]: Proxy,
  [AtomicItemListType.Empty]: Empty,
  [AtomicItemListType.NotificationGuide]: NotificationGuide,
  [AtomicItemListType.CompanyInfo]: CompanyInfo,
  [AtomicItemListType.SearchBar]: SearchBar,
  [AtomicItemListType.NeonSignBoard]: NeonSignBoard,
  [AtomicItemListType.Summary]: Summary,
  // Phase 2: Medium-Easy (단순 리스트형)
  [AtomicItemListType.HorizontalChipList]: HorizontalChipList,
  [AtomicItemListType.FourColIconList]: FourColIconList,
  [AtomicItemListType.FiveColIconList]: FiveColIconList,
  [AtomicItemListType.ScrollableIconList]: ScrollableIconList,
  [AtomicItemListType.MarketBadgeList]: MarketBadgeList,
  [AtomicItemListType.StoreList]: StoreList,
  [AtomicItemListType.TwoImageMarketCarousel]: TwoImageMarketCarousel,
  [AtomicItemListType.ThreeImageMarketCarousel]: ThreeImageMarketCarousel,
  [AtomicItemListType.RollingBannerList]: RollingBannerList,
  [AtomicItemListType.BannerCarousel]: BannerCarousel,
  [AtomicItemListType.DescriptionCardCarousel]: DescriptionCardCarousel,
  [AtomicItemListType.PhotoReviewCarousel]: PhotoReviewCarousel,
  [AtomicItemListType.VerticalBannerList]: VerticalBannerList,
};

export const FooterComponent = {
  [AtomicFooterType.More]: FooterMore,
};

export const createAtomicComponent = (params: {
  component: IScreensComponent;
  index: number;
  likeMap: Record<string, boolean>;
  setLikeMap: Dispatch<SetStateAction<Record<string, boolean>>>;
  scrollToFilterList?: VoidFunction;
  onReload?: () => void;
  screen?: string;
  logDisabled?: boolean;
}) => {
  const { component, likeMap, setLikeMap, scrollToFilterList, onReload } =
    params;
  const Header = component.type.header
    ? HeaderComponent[component.type.header]
    : null;

  const Footer = component.type.footer
    ? FooterComponent[component.type.footer]
    : null;

  const ItemList = component.type.itemList
    ? ItemListComponent[component.type.itemList]
    : null;

  return (
    <>
      {Header && (
        <Header
          screen={params.screen}
          header={component.entity.header}
          onReload={onReload}
        />
      )}
      {ItemList && component.entity.itemList && (
        <ItemList
          screen={params.screen}
          entity={component.entity.itemList}
          body={component.entity.body}
          logging={component.logging}
          scrollToFilterList={
            scrollToFilterList ? scrollToFilterList : () => {}
          }
          likeMap={likeMap}
          logDisabled={params.logDisabled}
          setLikeMap={setLikeMap}
        />
      )}
      {Footer && (
        <Footer screen={params.screen} footer={component.entity.footer} />
      )}
    </>
  );
};
